// Rewrites legacy global-endpoint AWS presigned URLs (bucket.s3.amazonaws.com) to the
// regional host the SigV4 signature expects. Any other host — including E2E Object Store
// presigned URLs (*.e2enetworks.net, path-style) — passes through untouched by design:
// presigned URLs from the backend are opaque and must not be modified.
const normalizeS3Url = (url) => {
  if (typeof url !== "string" || !url.includes(".s3.amazonaws.com")) return url;

  try {
    const parsedUrl = new URL(url);
    if (!parsedUrl.hostname.endsWith(".s3.amazonaws.com")) {
      return url;
    }

    const credentialParam = parsedUrl.searchParams.get("X-Amz-Credential");
    const credentialParts = credentialParam ? credentialParam.split("/") : [];
    const region = credentialParts.length >= 3 ? credentialParts[2] : null;

    if (!region) {
      return url;
    }

    const bucket = parsedUrl.hostname.replace(".s3.amazonaws.com", "");
    parsedUrl.hostname = `${bucket}.s3.${region}.amazonaws.com`;
    return parsedUrl.toString();
  } catch (error) {
    console.error("Failed to normalize S3 URL", error);
    return url;
  }
};

const resolveUrl = (url) => {
  if (!url) return url;
  return normalizeS3Url(url);
};

export const getMediaUrl = (mediaItem) => {

  if (!mediaItem) return null;
  if (mediaItem.url) return resolveUrl(mediaItem.url);
  if (mediaItem.content && mediaItem.contentType) {
    return `data:${mediaItem.contentType};base64,${mediaItem.content}`;
  }
  if (typeof mediaItem === "string") {
    const normalizedUrl = resolveUrl(mediaItem);
    if (
      normalizedUrl.startsWith("http://") ||
      normalizedUrl.startsWith("https://") ||
      normalizedUrl.startsWith("data:")
    ) {
      return normalizedUrl;
    }
  }
  return null;
};

/**
 * Detailed floor-plan uploads (S3 key/url segments). Config images use other paths.
 * Used by ConfigDetails carousel, phase thumbnails, CreateConfigModal, ConfigModal.
 */
export const isDetailedFloorPlanMedia = (mediaItem) => {
  const key = (mediaItem?.key || "").toLowerCase();
  const url = (mediaItem?.url || "").toLowerCase();
  return (
    key.includes("detailed-floor-plan") ||
    key.includes("detailed_floor_plan") ||
    key.includes("floor-plan") ||
    key.includes("floor_plan") ||
    key.includes("detailedfloorplan") ||
    url.includes("detailed_floor_plans") ||
    url.includes("detailed-floor-plans") ||
    url.includes("detailedfloorplan")
  );
};

export const mapMediaItems = (items = [], prefix = "Document") =>
  (Array.isArray(items) ? items : []).map((item, index) => {
    const fallbackName =
      item?.name ||
      (item?.key ? item.key.split("/").pop() : `${prefix} ${index + 1}`);
    return {
      ...item,
      url: getMediaUrl(item),
      name: fallbackName,
    };
  });

export const dedupeMediaByUrl = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const u = item?.url || getMediaUrl(item) || "";
    if (!u || seen.has(u)) return false;
    seen.add(u);
    return true;
  });
};

/** Unwrap GET/PUT project media payloads (array, { items }, nested marketing fields). */
export const normalizeProjectMediaList = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (response?.url && (response?.key || response?.contentType)) {
    return [response];
  }

  const nestedKeys = [
    "items",
    "data",
    "media",
    "marketingMedia",
    "marketing_media",
    "agreement",
    "agreements",
    "images",
  ];

  for (const key of nestedKeys) {
    const value = response[key];
    if (Array.isArray(value)) return value;
    if (value?.url && (value?.key || value?.contentType)) return [value];
  }

  if (response?.data && typeof response.data === "object") {
    return normalizeProjectMediaList(response.data);
  }

  return [];
};

export const isMarketingMediaItem = (item) => {
  const hint = `${item?.category || ""} ${item?.key || ""} ${item?.url || ""}`.toLowerCase();
  return hint.includes("marketing_media") || hint.includes("/marketing/");
};

const isNewPropertyConfigMediaEnvelope = (response) =>
  response &&
  typeof response === "object" &&
  !Array.isArray(response) &&
  (Object.prototype.hasOwnProperty.call(response, "propertyConfigPicture") ||
    Object.prototype.hasOwnProperty.call(response, "detailedFloorPlans") ||
    Object.prototype.hasOwnProperty.call(response, "property_config_picture") ||
    Object.prototype.hasOwnProperty.call(response, "detailed_floor_plans"));

/**
 * Normalizes GET /property/config/media (and similar) for UI:
 * - New: { propertyConfigPicture, detailedFloorPlans } — carousel uses image/* from pictures (non–floor-plan keys); floor grid uses detailedFloorPlans.
 * - Legacy: { items: [] } | [] | { data } — split with isDetailedFloorPlanMedia.
 */
export const normalizePropertyConfigMediaForUI = (response) => {
  if (response == null) {
    return { gallery: [], detailedFloorPlans: [] };
  }

  if (isNewPropertyConfigMediaEnvelope(response)) {
    const pics = mapMediaItems(
      response.propertyConfigPicture || response.property_config_picture || [],
      "Config"
    );
    const floors = mapMediaItems(
      response.detailedFloorPlans || response.detailed_floor_plans || [],
      "Floor plan"
    );
    const gallery = pics.filter(
      (m) =>
        (m.contentType || "").toLowerCase().startsWith("image/") &&
        !isDetailedFloorPlanMedia(m)
    );
    return {
      gallery: dedupeMediaByUrl(gallery),
      detailedFloorPlans: dedupeMediaByUrl(floors),
    };
  }

  let flat = [];
  if (response?.items && Array.isArray(response.items)) {
    flat = response.items;
  } else if (Array.isArray(response)) {
    flat = response;
  } else if (response?.data && Array.isArray(response.data)) {
    flat = response.data;
  } else if (response?.data?.items && Array.isArray(response.data.items)) {
    flat = response.data.items;
  }

  const mapped = mapMediaItems(flat, "Config");
  return {
    gallery: dedupeMediaByUrl(mapped.filter((m) => !isDetailedFloorPlanMedia(m))),
    detailedFloorPlans: dedupeMediaByUrl(
      mapped.filter((m) => isDetailedFloorPlanMedia(m))
    ),
  };
};

/** Flat list with isImage / isPdf for screens that merge gallery + floor plans in one state. */
export const flattenPropertyConfigMediaForLeadUI = (response) => {
  const { gallery, detailedFloorPlans } = normalizePropertyConfigMediaForUI(response);
  const seen = new Set();
  const out = [];
  for (const m of [...gallery, ...detailedFloorPlans]) {
    const u = m?.url || "";
    if (u && seen.has(u)) continue;
    if (u) seen.add(u);
    const ct = (m.contentType || "").toLowerCase();
    out.push({
      ...m,
      isImage: ct.startsWith("image/"),
      isPdf: m.contentType === "application/pdf",
    });
  }
  return out;
};

